import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import clinicAPI from "../../../services/clinicApi";
import { toDateInputValue, formatTimeRu, formatDateRu } from "../../../utils/appointmentLabels";
import { hasAdminAccess } from "../../../utils/roles";
import L from "../../../constants/baysportLabels";
import s from "../clinic.module.css";
import aa from "../AdminPortal/adminApp.module.css";
import {
  Button,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  useToast,
} from "../../ui";
import SessionDetailModal from "./SessionDetailModal";
import DayScheduleView from "./DayScheduleView";
import WeekTimeGridView from "./WeekTimeGridView";
import CalendarSessionSheet from "./CalendarSessionSheet";
import CalendarCreateSheet from "./CalendarCreateSheet";
import CalendarCreateKindSheet from "./CalendarCreateKindSheet";
import SessionDateTimeFields from "./SessionDateTimeFields";
import ClientSearchPicker from "../common/ClientSearchPicker";
import {
  calendarEntryTitle,
  INDIVIDUAL_SESSION_COLOR,
} from "../../../constants/calendarSessionTypes";
import {
  addDays,
  isIndividualSession,
  isToday,
  monthGridDays,
  sessionColor,
  sessionLabel,
  sessionScheduleToIso,
  weekStart,
} from "./calendarUtils";
import { competitionPayloadFromForm, clubEventTag } from "../../../utils/competitionUtils";
import { sessionKindsOnDay } from "../../../utils/clientScheduleUtils";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { useIsAdminApp } from "../../../hooks/useStandalone";
import { useSwipeNavigation } from "../../../hooks/useSwipeNavigation";
import { IconPlus } from "../../ui/icons";
import { createPortal } from "react-dom";
import md from "../common/manageDesktop.module.css";

const VIEWS = [
  { id: "month", label: "Месяц" },
  { id: "day", label: "День" },
  { id: "week", label: "Неделя" },
  { id: "three", label: "3 дня" },
];

const COMPACT_VIEWS = [
  { id: "day", label: "Неделя" },
  { id: "month", label: "Месяц" },
];

const FORMAT_FILTER_OPTIONS = [
  { id: "", label: "Все типы" },
  { id: "group", label: "Групповые" },
  { id: "individual", label: "Индивидуальные" },
  { id: "event", label: "События" },
];

function daySessions(sessions, day) {
  return sessions
    .filter((sess) => sess.start_datetime?.slice(0, 10) === day)
    .sort(
      (a, b) =>
        new Date(a.start_datetime) - new Date(b.start_datetime),
    );
}

function CalendarColorLegend({ groups, groupColorMap }) {
  if (!groups.length) return null;
  return (
    <div className={aa.calColorLegend} role="list" aria-label="Обозначения цветов">
      {groups.map((g, i) => (
        <span key={g.id} className={aa.calColorLegendItem} role="listitem">
          <span
            className={aa.calColorLegendDot}
            style={{
              background:
                groupColorMap[g.id] ||
                sessionColor({ group: g, session_kind: "training" }, i),
            }}
          />
          <span className={aa.calColorLegendLabel}>{g.name}</span>
        </span>
      ))}
      <span className={aa.calColorLegendItem} role="listitem">
        <span
          className={aa.calColorLegendDot}
          style={{ background: INDIVIDUAL_SESSION_COLOR }}
        />
        <span className={aa.calColorLegendLabel}>Индивидуальная</span>
      </span>
      <span className={aa.calColorLegendItem} role="listitem">
        <span
          className={aa.calColorLegendDot}
          style={{ background: "#64748b" }}
        />
        <span className={aa.calColorLegendLabel}>Событие</span>
      </span>
    </div>
  );
}

function AdminCalLegend() {
  return (
    <div className={aa.calAppLegend} aria-label="Обозначения в календаре">
      <span className={aa.calAppLegendItem}>
        <i
          className={`${aa.calAppLegendDot} ${aa.calAppLegendDotTraining}`}
          aria-hidden
        />
        Тренировка
      </span>
      <span className={aa.calAppLegendItem}>
        <i
          className={`${aa.calAppLegendDot} ${aa.calAppLegendDotCompetition}`}
          aria-hidden
        />
        Событие
      </span>
    </div>
  );
}

function AdminCalWeekStrip({ anchor, sessions, onPick }) {
  const weekStartDay = weekStart(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStartDay, i));

  return (
    <div className={aa.calAppDayStrip}>
      {days.map((day) => {
        const active = day === anchor;
        const kinds = sessionKindsOnDay(sessions, new Date(`${day}T12:00:00`));
        const d = new Date(`${day}T12:00:00`);
        return (
          <button
            key={day}
            type="button"
            className={`${aa.calAppStripDay} ${active ? aa.calAppStripDayOn : ""}`}
            onClick={() => onPick(day)}
          >
            <span>{d.toLocaleDateString("ru-RU", { weekday: "narrow" })}</span>
            <strong>{d.getDate()}</strong>
            {kinds.training > 0 || kinds.competition > 0 ? (
              <span className={aa.calAppStripMarkers}>
                {kinds.training > 0 ? (
                  <i
                    className={`${aa.calAppStripDot} ${aa.calAppStripDotTraining}`}
                  />
                ) : null}
                {kinds.competition > 0 ? (
                  <i
                    className={`${aa.calAppStripDot} ${aa.calAppStripDotCompetition}`}
                  />
                ) : null}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function AdminMonthGrid({ days, anchor, filtered, loading, onDayClick }) {
  return (
    <div className={aa.calAppMonthPanel}>
      <div className={aa.calAppMonthWeekdaysRow}>
        {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((w) => (
          <div key={w} className={aa.calAppWeekday}>
            {w}
          </div>
        ))}
      </div>
      <div
        key={anchor.slice(0, 7)}
        className={[
          aa.calAppMonthCellsGrid,
          aa.calAppMonthAnim,
          loading ? aa.calAppMonthLoading : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {days.map((day) => {
          const inMonth = day.slice(0, 7) === anchor.slice(0, 7);
          const dayKinds = sessionKindsOnDay(
            filtered,
            new Date(`${day}T12:00:00`),
          );
          return (
            <button
              key={day}
              type="button"
              className={[
                aa.calAppCell,
                !inMonth ? aa.calAppCellMuted : "",
                day === anchor ? aa.calAppCellOn : "",
                isToday(day) ? aa.calAppCellToday : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onDayClick(day)}
            >
              <span className={aa.calAppDayNum}>
                {Number(day.slice(8, 10))}
              </span>
              <div className={aa.calAppDots}>
                {dayKinds.training > 0 ? (
                  <i
                    className={`${aa.calAppDot} ${aa.calAppStripDotTraining}`}
                    style={{ background: "var(--aa-accent)" }}
                  />
                ) : null}
                {dayKinds.competition > 0 ? (
                  <i
                    className={`${aa.calAppDot} ${aa.calAppStripDotCompetition}`}
                    style={{ background: "#d97706" }}
                  />
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AdminCalEventCard({ session, groups, onOpen }) {
  const isEvent = session.session_kind === "event";
  const isCompetition = isEvent && session.event_kind === "competition";
  const eventTag = clubEventTag(session);
  const title = sessionLabel(session);
  const accentStyle = isCompetition
    ? { "--cal-accent": "#d97706" }
    : {
        "--cal-accent": sessionColor(
          session,
          groups.findIndex((g) => g.id === session.group?.id),
        ),
      };

  return (
    <button
      type="button"
      className={`${aa.calAppEvent} ${isCompetition ? aa.calAppEventCompetition : ""}`}
      onClick={() => onOpen(session)}
      style={accentStyle}
    >
      <span className={aa.calAppEventAccent} aria-hidden />
      <div className={aa.calAppEventMain}>
        <div className={aa.calAppEventHead}>
          <span className={aa.calAppEventTime}>
            {formatTimeRu(session.start_datetime)}
          </span>
        </div>
        <span
          className={
            isCompetition
              ? aa.calAppEventTypeCompetition
              : aa.calAppEventTypeTraining
          }
        >
          {isCompetition ? eventTag || "Соревнование" : "Тренировка"}
        </span>
        <h3 className={aa.calAppEventTitle}>{title}</h3>
        <div className={aa.calAppEventMeta}>
          {session.branch?.name ? <span>{session.branch.name}</span> : null}
          {session.trainer?.user?.full_name ? (
            <span>{session.trainer.user.full_name}</span>
          ) : null}
        </div>
      </div>
      <span className={aa.calAppEventChevron} aria-hidden>
        ›
      </span>
    </button>
  );
}

function AdminCalDayBody({ day, sessions, groups, onOpenSession }) {
  const list = daySessions(sessions, day);
  const dayDate = new Date(`${day}T12:00:00`);
  const today = isToday(day);

  return (
    <div className={aa.calAppBody}>
      <div className={aa.calAppDayHeader}>
        <p className={aa.calAppDayTitle}>
          {formatDateRu(dayDate, {
            weekday: "long",
            day: "numeric",
            month: "long",
          })}
        </p>
        {today ? <span className={aa.calAppDayBadge}>Сегодня</span> : null}
        {list.length > 0 ? (
          <span className={aa.calAppDayCount}>
            {list.length} {list.length === 1 ? "событие" : "события"}
          </span>
        ) : null}
      </div>
      {list.length === 0 ? (
        <p className={aa.calAppMonthEmptyHint}>На этот день занятий нет</p>
      ) : (
        <div className={aa.calAppEventList}>
          {list.map((sess) => (
            <AdminCalEventCard
              key={sess.id}
              session={sess}
              groups={groups}
              onOpen={onOpenSession}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrainerCalendar({ staffMode = false }) {
  const toast = useToast();
  const { role, trainerProfile } = useSelector((state) => state.auth);
  const isMobile = useIsMobile(768);
  const isAdminApp = useIsAdminApp(hasAdminAccess(role));
  const isAdminMobile = isAdminApp && isMobile;
  const useSheetUx = isMobile;
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get("date") || toDateInputValue();
  const initialView = searchParams.get("view");
  const initialSession = searchParams.get("session");
  const initialGroup = searchParams.get("group");
  const initialTrainer = searchParams.get("trainer");
  const initialCreate = searchParams.get("create");
  const initialPatient = searchParams.get("patient");

  const [view, setView] = useState(() => {
    if (COMPACT_VIEWS.some((v) => v.id === initialView)) return initialView;
    if (VIEWS.some((v) => v.id === initialView)) return initialView;
    return "day";
  });
  const [anchor, setAnchor] = useState(initialDate);
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [branches, setBranches] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [groupFilter, setGroupFilter] = useState(initialGroup || "");
  const [trainerFilter, setTrainerFilter] = useState(initialTrainer || "");
  const [formatFilter, setFormatFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [selectedDayId, setSelectedDayId] = useState(initialSession || null);
  const [sheet, setSheet] = useState(null);
  const [nearestBusy, setNearestBusy] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [createKind, setCreateKind] = useState(null);
  const [createPickerOpen, setCreatePickerOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createCoverFile, setCreateCoverFile] = useState(null);
  const [createCoverPreview, setCreateCoverPreview] = useState(null);
  const [createForm, setCreateForm] = useState({
    specialization_id: "",
    doctor_id: "",
    branch_id: "",
    patient_id: "",
    sessionDate: "",
    startTime: "17:00",
    endTime: "18:00",
    title: "",
    event_kind: "competition",
    notes: "",
    venue_name: "",
    venue_address: "",
    assembly_date: "",
    assembly_time: "",
    event_description: "",
    contact_info: "",
  });

  const days = useMemo(() => {
    if (view === "day") {
      if (isAdminMobile) {
        const start = weekStart(anchor);
        return Array.from({ length: 7 }, (_, i) => addDays(start, i));
      }
      return [anchor];
    }
    if (view === "three") {
      return [anchor, addDays(anchor, 1), addDays(anchor, 2)];
    }
    if (view === "month") return monthGridDays(anchor);
    const start = weekStart(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [view, anchor, isAdminMobile]);

  const rangeFrom = view === "month" ? days[0] : days[0];
  const rangeTo = view === "month" ? days[days.length - 1] : days[days.length - 1];

  const groupColorMap = useMemo(() => {
    const map = {};
    groups.forEach((g, i) => {
      map[g.id] = sessionColor({ group: g, session_kind: "training" }, i);
    });
    return map;
  }, [groups]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sessionParams = { from: rangeFrom, to: rangeTo };
      if (trainerFilter) sessionParams.trainer_id = trainerFilter;
      const requests = [
        clinicAPI.getTrainingSessions(sessionParams),
        clinicAPI.getSpecializations(),
        clinicAPI.getBranches(),
      ];
      if (!staffMode) {
        requests.push(clinicAPI.getDoctors({ include_summary: "false" }));
      }
      const results = await Promise.all(requests);
      const sess = results[0];
      const gr = results[1];
      const br = results[2];
      const doc = staffMode ? null : results[3];
      setSessions(sess.sessions || []);
      setGroups((gr.specializations || []).filter((g) => g.is_active !== false));
      setBranches((br.branches || []).filter((b) => b.is_active !== false));
      setDoctors(doc?.doctors || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [rangeFrom, rangeTo, trainerFilter, staffMode, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useAdminRefresh(load);

  useEffect(() => {
    if (staffMode && trainerProfile?.id) {
      setTrainerFilter(String(trainerProfile.id));
    }
  }, [staffMode, trainerProfile?.id]);

  useEffect(() => {
    if (initialSession && initialDate) {
      if (useSheetUx) {
        setView("month");
        setAnchor(initialDate);
        setSheet({ day: initialDate, sessionId: initialSession });
      } else if (initialView === "day" || !initialView) {
        setView("day");
        setSelectedDayId(initialSession);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialCreate === "individual") {
      setCreateKind("individual");
      setCreateForm((prev) => ({
        ...prev,
        patient_id: initialPatient || prev.patient_id,
        sessionDate: initialDate,
      }));
    } else if (initialCreate === "event") {
      setCreateKind("event");
      setCreateForm((prev) => ({
        ...prev,
        sessionDate: initialDate,
        event_kind: "competition",
      }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view !== "day") {
      setSelectedDayId(null);
    }
  }, [view]);

  const viewOptions = useSheetUx ? COMPACT_VIEWS : VIEWS;

  const openDaySheet = (day, sessionId = null) => {
    setSheet({ day, sessionId });
  };

  const closeDaySheet = () => setSheet(null);

  const openSessionInDayView = (sess) => {
    const day =
      sess.start_datetime?.slice(0, 10) ||
      sess.session_date?.slice?.(0, 10) ||
      sess.session_date;
    if (day) setAnchor(day);
    if (useSheetUx) {
      openDaySheet(day, sess.id);
      return;
    }
    setSelectedDayId(sess.id);
    setView("day");
  };

  const applySessionFilters = useCallback(
    (list) => {
      let out = list;
      if (formatFilter === "group") {
        out = out.filter(
          (sess) =>
            sess.session_kind === "training" && !isIndividualSession(sess),
        );
      } else if (formatFilter === "individual") {
        out = out.filter((sess) => isIndividualSession(sess));
      } else if (formatFilter === "event") {
        out = out.filter((sess) => sess.session_kind === "event");
      }
      if (groupFilter) {
        out = out.filter(
          (sess) =>
            sess.specialization_id === groupFilter ||
            sess.group?.id === groupFilter,
        );
      }
      if (trainerFilter) {
        out = out.filter(
          (sess) =>
            sess.doctor_id === trainerFilter ||
            sess.trainer?.id === trainerFilter,
        );
      }
      return out;
    },
    [formatFilter, groupFilter, trainerFilter],
  );

  const goToNearestTraining = async () => {
    if (nearestBusy) return;
    setNearestBusy(true);
    try {
      const today = toDateInputValue();
      const params = { from: today, to: addDays(today, 120) };
      if (trainerFilter) params.trainer_id = trainerFilter;
      const r = await clinicAPI.getTrainingSessions(params);
      const now = new Date();
      const next = applySessionFilters(r.sessions || [])
        .filter(
          (sess) =>
            sess.status !== "cancelled" &&
            sess.start_datetime &&
            new Date(sess.start_datetime) >= now,
        )
        .sort(
          (a, b) => new Date(a.start_datetime) - new Date(b.start_datetime),
        )[0];

      if (!next) {
        toast.info("Нет предстоящих занятий");
        return;
      }

      const day = next.start_datetime.slice(0, 10);
      setAnchor(day);
      if (useSheetUx) {
        if (view !== "month") setView("month");
        openDaySheet(day, next.id);
      } else {
        setSelectedDayId(next.id);
        setView("day");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось найти занятие");
    } finally {
      setNearestBusy(false);
    }
  };

  const filtered = useMemo(
    () => applySessionFilters(sessions),
    [sessions, applySessionFilters],
  );

  const shiftAnchor = (delta) => {
    if (view === "month") {
      const d = new Date(`${anchor.slice(0, 7)}-01T12:00:00`);
      d.setMonth(d.getMonth() + delta);
      setAnchor(toDateInputValue(d));
      return;
    }
    const step =
      view === "week" || (isAdminMobile && view === "day")
        ? 7
        : view === "three"
          ? 3
          : 1;
    setAnchor(addDays(anchor, delta * step));
  };

  const clearCreateCover = () => {
    if (createCoverPreview) URL.revokeObjectURL(createCoverPreview);
    setCreateCoverFile(null);
    setCreateCoverPreview(null);
  };

  const openCreate = (kind) => {
    clearCreateCover();
    setCreatePickerOpen(false);
    setCreateForm({
      specialization_id: groups[0]?.id || "",
      doctor_id: doctors[0]?.id || "",
      branch_id: branches[0]?.id || "",
      patient_id: "",
      sessionDate: anchor,
      startTime: "17:00",
      endTime: "18:00",
      title: "",
      event_kind: "competition",
      notes: "",
      venue_name: "",
      venue_address: "",
      assembly_date: "",
      assembly_time: "",
      event_description: "",
      contact_info: "",
    });
    setCreateKind(kind);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (createKind === "individual" && !createForm.patient_id) {
      toast.error("Выберите клиента");
      return;
    }
    if (!createForm.doctor_id) {
      toast.error(`Выберите ${L.trainer.toLowerCase()}`);
      return;
    }
    setCreateSaving(true);
    try {
      const { start, end } = sessionScheduleToIso({
        date: createForm.sessionDate,
        startTime: createForm.startTime,
        endTime: createForm.endTime,
      });
      if (!start || !end) {
        toast.error("Укажите дату и время занятия");
        setCreateSaving(false);
        return;
      }
      if (createKind === "event" && !createForm.title?.trim()) {
        toast.error("Укажите название события");
        setCreateSaving(false);
        return;
      }
      const body = {
        doctor_id: createForm.doctor_id,
        branch_id: createForm.branch_id || null,
        start_datetime: start,
        end_datetime: end,
        notes: createForm.notes || null,
      };
      if (createKind === "event") {
        body.session_kind = "event";
        body.title = createForm.title.trim();
        body.event_kind = createForm.event_kind;
        if (createForm.event_kind === "competition") {
          Object.assign(body, competitionPayloadFromForm(createForm));
          body.branch_id = null;
        }
      } else if (createKind === "individual") {
        body.session_kind = "training";
        body.training_format = "individual";
        body.patient_id = createForm.patient_id;
      } else {
        body.specialization_id = createForm.specialization_id;
        body.session_kind = "training";
        body.training_format = "group";
      }
      const created = await clinicAPI.createTrainingSession(body);
      const sessionId = created?.session?.id;
      if (
        createKind === "event" &&
        createForm.event_kind === "competition" &&
        createCoverFile &&
        sessionId
      ) {
        try {
          await clinicAPI.uploadSessionCover(sessionId, createCoverFile);
        } catch (coverErr) {
          toast.error(
            coverErr.response?.data?.error ||
              "Событие создано, но обложка не загрузилась",
          );
        }
      }
      const labels = {
        event: "Событие создано",
        individual: "Индивидуальная тренировка создана",
        training: "Занятие создано",
      };
      toast.success(labels[createKind] || "Создано");
      setCreateKind(null);
      clearCreateCover();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка");
    } finally {
      setCreateSaving(false);
    }
  };

  const monthAnchorLabel = new Date(`${anchor.slice(0, 7)}-01T12:00:00`).toLocaleDateString(
    "ru-RU",
    { month: "long", year: "numeric" },
  );

  const monthAnchorShort = new Date(`${anchor.slice(0, 7)}-01T12:00:00`).toLocaleDateString(
    "ru-RU",
    { month: "long" },
  );

  const weekLabel = useMemo(() => {
    const start = weekStart(anchor);
    const end = addDays(start, 6);
    return `${formatDateRu(start, { day: "numeric", month: "short" })} — ${formatDateRu(end, { day: "numeric", month: "short" })}`;
  }, [anchor]);

  const filtersActive = Boolean(groupFilter || trainerFilter);

  const handleMonthDayClick = (day) => {
    setAnchor(day);
    if (isAdminMobile) {
      return;
    }
    if (isAdminApp) {
      setView("day");
      return;
    }
    if (useSheetUx) {
      const list = daySessions(filtered, day);
      if (list.length === 1) {
        openDaySheet(day, list[0].id);
      } else {
        openDaySheet(day);
      }
      return;
    }
    setView("day");
  };

  const handleMonthSessionClick = (e, day, sess) => {
    e.stopPropagation();
    setAnchor(day);
    if (isAdminMobile) {
      openDaySheet(day, sess.id);
      return;
    }
    if (isAdminApp) {
      setSelectedDayId(sess.id);
      setView("day");
      return;
    }
    if (useSheetUx) {
      openDaySheet(day, sess.id);
      return;
    }
    setSelectedDayId(sess.id);
    setView("day");
  };

  const monthSwipe = useSwipeNavigation({
    enabled: isAdminMobile && view === "month",
    onPrev: () => shiftAnchor(-1),
    onNext: () => shiftAnchor(1),
  });

  const patchCreateForm = (patch) =>
    setCreateForm((prev) => ({ ...prev, ...patch }));

  const handleCreateCoverFile = (file) => {
    if (createCoverPreview) URL.revokeObjectURL(createCoverPreview);
    setCreateCoverFile(file);
    setCreateCoverPreview(URL.createObjectURL(file));
  };

  return (
    <div
      className={[
        s.sessionCalRoot,
        useSheetUx && !isAdminApp ? s.sessionCalRootMobile : "",
        isAdminApp ? aa.calApp : "",
        isAdminApp ? aa.calRootApp : "",
        isAdminApp && !isMobile ? md.desktopCalendar : "",
        isAdminMobile && (view === "day" || view === "month")
          ? aa.calAppContentMode
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
      {...(isAdminMobile ? { "data-pull-scroll": true } : {})}
    >
      {isAdminMobile ? (
        <header className={aa.calAppHeader}>
          <div className={aa.calAppHeadTop}>
            <h1 className={aa.calAppHeadTitle}>Календарь</h1>
            <div className={aa.calAppViewPills}>
              {COMPACT_VIEWS.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  className={
                    view === v.id ? aa.calAppViewPillActive : aa.calAppViewPill
                  }
                  onClick={() => setView(v.id)}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div className={aa.calAppHeadNav}>
            <button
              type="button"
              className={aa.calAppNavBtn}
              onClick={() => shiftAnchor(-1)}
              aria-label={
                view === "month" ? "Предыдущий месяц" : "Предыдущая неделя"
              }
            >
              ‹
            </button>
            <button
              type="button"
              className={aa.calAppHeadRangeBtn}
              onClick={() => setAnchor(toDateInputValue())}
            >
              {view === "month" ? monthAnchorLabel : weekLabel}
            </button>
            <button
              type="button"
              className={aa.calAppNavBtn}
              onClick={() => shiftAnchor(1)}
              aria-label={
                view === "month" ? "Следующий месяц" : "Следующая неделя"
              }
            >
              ›
            </button>
            <button
              type="button"
              className={`${aa.calAppIconBtn} ${filtersActive ? aa.calAppIconBtnActive : ""}`}
              onClick={() => setFilterOpen(true)}
              aria-label="Фильтры"
            >
              <svg viewBox="0 0 24 24" aria-hidden>
                <path
                  d="M4 6h16M7 12h10M10 18h4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </header>
      ) : (
      <div
        className={[
          s.sessionCalPanel,
          isAdminApp ? aa.calPanelApp : "",
          isAdminApp && !isMobile ? md.desktopCalPanel : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
      <div
        className={[
          s.sessionCalToolbar,
          useSheetUx ? s.sessionCalToolbarMobile : "",
          isAdminApp ? aa.calToolbarApp : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className={s.sessionCalToolbarNav}>
          <Button type="button" variant="secondary" size="sm" onClick={() => shiftAnchor(-1)}>
            ←
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setAnchor(toDateInputValue())}
          >
            Сегодня
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => shiftAnchor(1)}>
            →
          </Button>
          {view === "month" ? (
            <span className={`${s.calRangeLabel} ${isAdminApp ? aa.calMonthTitle : ""}`}>
              {monthAnchorLabel}
            </span>
          ) : (
            <Input
              type="date"
              value={anchor}
              onChange={(e) => setAnchor(e.target.value)}
              className={useSheetUx ? s.calDateInputMobile : undefined}
            />
          )}
        </div>
        {useSheetUx ? (
          <div className={aa.calViewSegment}>
            {viewOptions.map((v) => (
              <button
                key={v.id}
                type="button"
                className={
                  view === v.id ? aa.calViewSegmentActive : aa.calViewSegmentBtn
                }
                onClick={() => setView(v.id)}
              >
                {v.label}
              </button>
            ))}
          </div>
        ) : (
          <div className={s.calViewTabs}>
            {viewOptions.map((v) => (
              <button
                key={v.id}
                type="button"
                className={view === v.id ? s.calViewTabActive : s.calViewTab}
                onClick={() => setView(v.id)}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
        <div className={s.sessionCalToolbarActions}>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={nearestBusy}
            onClick={goToNearestTraining}
          >
            {nearestBusy ? "…" : "Ближайшая"}
          </Button>
          <Button type="button" size="sm" onClick={() => setCreatePickerOpen(true)}>
            + Добавить
          </Button>
        </div>
      </div>

      {isMobile && !isAdminApp ? (
        <div className={s.calFiltersRow}>
          <Field label="Тип">
            <Select
              value={formatFilter}
              onChange={(e) => setFormatFilter(e.target.value)}
            >
              {FORMAT_FILTER_OPTIONS.map((opt) => (
                <option key={opt.id || "all"} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Field>
          {groups.length > 0 ? (
            <Field label={L.group}>
              <Select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
              >
                <option value="">Все {L.groups.toLowerCase()}</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {!staffMode && doctors.length > 0 ? (
            <Field label={L.trainer}>
              <Select
                value={trainerFilter}
                onChange={(e) => setTrainerFilter(e.target.value)}
              >
                <option value="">Все {L.trainers.toLowerCase()}</option>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.full_name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
        </div>
      ) : null}

      {!isMobile && groups.length > 0 ? (
        <div className={s.calLegend}>
          <button
            type="button"
            className={`${s.calLegendItem} ${!groupFilter ? s.calLegendItemActive : ""}`}
            onClick={() => setGroupFilter("")}
          >
            Все группы
          </button>
          {groups.map((g, i) => (
            <button
              key={g.id}
              type="button"
              className={`${s.calLegendItem} ${groupFilter === g.id ? s.calLegendItemActive : ""}`}
              onClick={() => setGroupFilter(g.id)}
            >
              <span
                className={s.calLegendDot}
                style={{ background: groupColorMap[g.id] || sessionColor({ group: g }, i) }}
              />
              {g.name}
            </button>
          ))}
        </div>
      ) : null}

      {!isMobile ? (
        <div className={s.calLegend}>
          {FORMAT_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.id || "all"}
              type="button"
              className={`${s.calLegendItem} ${formatFilter === opt.id ? s.calLegendItemActive : ""}`}
              onClick={() => setFormatFilter(opt.id)}
            >
              {opt.id === "individual" ? (
                <span
                  className={s.calLegendDot}
                  style={{ background: INDIVIDUAL_SESSION_COLOR }}
                />
              ) : opt.id === "event" ? (
                <span
                  className={s.calLegendDot}
                  style={{ background: "#64748b" }}
                />
              ) : null}
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}

      {!staffMode && !isMobile && doctors.length > 0 ? (
        <div className={s.calLegend}>
          <span className={s.calLegendLabel}>{L.trainer}:</span>
          <button
            type="button"
            className={`${s.calLegendItem} ${!trainerFilter ? s.calLegendItemActive : ""}`}
            onClick={() => setTrainerFilter("")}
          >
            Все
          </button>
          {doctors.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`${s.calLegendItem} ${trainerFilter === d.id ? s.calLegendItemActive : ""}`}
              onClick={() => setTrainerFilter(d.id)}
            >
              {d.full_name}
            </button>
          ))}
        </div>
      ) : null}

      {!isAdminApp && !isMobile ? (
      <p className={s.docSectionHint}>
        {view === "day"
          ? "Режим дня: слоты по часам слева, группа и перекличка справа. Шаблон расписания — в карточке группы."
          : "Шаблон расписания — в карточке группы. Здесь — живой календарь: перекличка, перенос и отмена только выбранного занятия."}
      </p>
      ) : null}
      </div>
      )}

      {isAdminMobile && (view === "day" || view === "month") ? (
        <div
          className={aa.calAppContent}
          onTouchStart={view === "month" ? monthSwipe.onTouchStart : undefined}
          onTouchEnd={view === "month" ? monthSwipe.onTouchEnd : undefined}
        >
          {view === "day" ? (
            <>
              <AdminCalWeekStrip
                anchor={anchor}
                sessions={filtered}
                onPick={setAnchor}
              />
              <AdminCalLegend />
            </>
          ) : (
            <>
              <AdminMonthGrid
                days={days}
                anchor={anchor}
                filtered={filtered}
                loading={loading}
                onDayClick={handleMonthDayClick}
              />
              {!loading && filtered.length === 0 ? (
                <p className={aa.calAppMonthEmptyHint}>
                  Нет занятий в этом месяце
                </p>
              ) : null}
              <AdminCalLegend />
            </>
          )}
          <AdminCalDayBody
            day={anchor}
            sessions={filtered}
            groups={groups}
            onOpenSession={(sess) => openDaySheet(anchor, sess.id)}
          />
        </div>
      ) : view === "month" ? (
        <div>
        {loading ? (
          <p className={s.catalogMuted}>Загрузка календаря…</p>
        ) : (
        <>
        <div
          key={anchor.slice(0, 7)}
          className={[s.calMonth, useSheetUx ? s.calMonthCompact : ""]
            .filter(Boolean)
            .join(" ")}
        >
          {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((w) => (
            <div key={w} className={s.calMonthWeekday}>
              {w}
            </div>
          ))}
          {days.map((day) => {
            const list = daySessions(filtered, day);
            const inMonth = day.slice(0, 7) === anchor.slice(0, 7);
            const hasBacklog = list.some((sess) => sess.needs_attendance);
            const allDone =
              list.length > 0 &&
              list.every(
                (sess) =>
                  sess.session_kind === "event" ||
                  sess.attendance_complete ||
                  !sess.expected_count,
              );
            const showPills = useSheetUx && list.length > 0;
            const visible = showPills ? list.slice(0, 2) : list.slice(0, 4);
            const extra = list.length - visible.length;
            return (
              <button
                key={day}
                type="button"
                className={[
                  s.calMonthCell,
                  !inMonth ? s.calMonthCellMuted : "",
                  isToday(day) ? s.calMonthCellToday : "",
                  hasBacklog ? s.calMonthCellBacklog : "",
                  allDone && inMonth && list.length ? s.calMonthCellDone : "",
                  showPills ? s.calMonthCellRich : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleMonthDayClick(day)}
              >
                <span className={s.calMonthDayNum}>
                  {Number(day.slice(8, 10))}
                </span>
                {showPills ? (
                  <div className={s.calMonthEvents}>
                    {visible.map((sess) => (
                      <span
                        key={sess.id}
                        className={s.calMonthEventPill}
                        style={{
                          "--session-color": sessionColor(
                            sess,
                            groups.findIndex((g) => g.id === sess.group?.id),
                          ),
                        }}
                        title={sessionLabel(sess)}
                        onClick={(e) => handleMonthSessionClick(e, day, sess)}
                      >
                        <span className={s.calMonthEventTime}>
                          {formatTimeRu(sess.start_datetime)}
                        </span>
                        <span className={s.calMonthEventTitle}>
                          {sessionLabel(sess)}
                        </span>
                      </span>
                    ))}
                    {extra > 0 ? (
                      <span className={s.calMonthEventMore}>+{extra}</span>
                    ) : null}
                  </div>
                ) : (
                  <div className={s.calMonthDots}>
                    {visible.map((sess) => (
                      <span
                        key={sess.id}
                        className={s.calMonthDot}
                        style={{
                          background: sessionColor(
                            sess,
                            groups.findIndex((g) => g.id === sess.group?.id),
                          ),
                        }}
                        title={sessionLabel(sess)}
                        onClick={(e) => handleMonthSessionClick(e, day, sess)}
                      />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {groups.length > 0 ? (
          <CalendarColorLegend groups={groups} groupColorMap={groupColorMap} />
        ) : null}
        </>
        )}
        </div>
      ) : loading ? (
        <p className={s.catalogMuted}>Загрузка календаря…</p>
      ) : view === "day" ? (
        <DayScheduleView
          day={anchor}
          sessions={filtered}
          groups={groups}
          branches={branches}
          selectedId={selectedDayId}
          onSelect={setSelectedDayId}
          onUpdated={load}
          detailMode={useSheetUx ? "sheet" : "aside"}
          onOpenDetail={(id) => openDaySheet(anchor, id)}
          appCompact={false}
        />
      ) : (
        <WeekTimeGridView
          days={days}
          sessions={filtered}
          groups={groups}
          onOpenSession={openSessionInDayView}
          onUpdated={load}
        />
      )}

      {!loading && filtered.length === 0 && view !== "day" && !isAdminMobile ? (
        <EmptyState
          title="Нет занятий в этом периоде"
          description="Настройте расписание группы или добавьте разовое занятие"
          actionLabel="+ Добавить"
          onAction={() => setCreatePickerOpen(true)}
        />
      ) : null}

      <CalendarCreateKindSheet
        open={createPickerOpen}
        onClose={() => setCreatePickerOpen(false)}
        onPick={openCreate}
      />

      {isAdminMobile && !createKind && !createPickerOpen
        ? createPortal(
            <button
              type="button"
              className={aa.calFab}
              onClick={() => setCreatePickerOpen(true)}
              aria-label="Добавить в календарь"
            >
              <IconPlus width={22} height={22} />
            </button>,
            document.body,
          )
        : null}

      {isAdminApp ? (
        <CalendarCreateSheet
          open={Boolean(createKind)}
          kind={createKind}
          form={createForm}
          onChange={patchCreateForm}
          groups={groups}
          branches={branches}
          doctors={doctors}
          saving={createSaving}
          coverPreviewUrl={createCoverPreview}
          onCoverFileSelect={handleCreateCoverFile}
          onClose={() => {
            clearCreateCover();
            setCreateKind(null);
          }}
          onSubmit={submitCreate}
        />
      ) : null}

      {isAdminMobile && filterOpen
        ? createPortal(
            <div
              className={aa.calSheetBackdrop}
              onClick={() => setFilterOpen(false)}
              role="presentation"
            >
              <div
                className={aa.calFilterSheet}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Фильтры"
              >
                <div className={aa.calSheetHandle} aria-hidden />
                <header className={aa.calFilterHead}>
                  <h2 className={aa.calFilterTitle}>Фильтры</h2>
                  <button
                    type="button"
                    className={aa.calSheetClose}
                    onClick={() => setFilterOpen(false)}
                    aria-label="Закрыть"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden>
                      <path
                        d="M6 6l12 12M18 6 6 18"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </header>
                <div className={aa.calFilterBody}>
                  <button
                    type="button"
                    className={aa.calFilterNearest}
                    disabled={nearestBusy}
                    onClick={async () => {
                      await goToNearestTraining();
                      setFilterOpen(false);
                    }}
                  >
                    {nearestBusy ? "Поиск…" : "Ближайшая тренировка"}
                  </button>
                  <Field label="Тип">
                    <Select
                      value={formatFilter}
                      onChange={(e) => setFormatFilter(e.target.value)}
                    >
                      {FORMAT_FILTER_OPTIONS.map((opt) => (
                        <option key={opt.id || "all"} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {groups.length > 0 ? (
                    <Field label={L.group}>
                      <Select
                        value={groupFilter}
                        onChange={(e) => setGroupFilter(e.target.value)}
                      >
                        <option value="">Все {L.groups.toLowerCase()}</option>
                        {groups.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : null}
                  {!staffMode && doctors.length > 0 ? (
                    <Field label={L.trainer}>
                      <Select
                        value={trainerFilter}
                        onChange={(e) => setTrainerFilter(e.target.value)}
                      >
                        <option value="">
                          Все {L.trainers.toLowerCase()}
                        </option>
                        {doctors.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.full_name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : null}
                </div>
                <footer className={aa.calFilterFoot}>
                  <button
                    type="button"
                    className={aa.calFilterReset}
                    onClick={() => {
                      setGroupFilter("");
                      if (!staffMode) setTrainerFilter("");
                      setFormatFilter("");
                    }}
                  >
                    Сбросить
                  </button>
                  <button
                    type="button"
                    className={aa.calFilterApply}
                    onClick={() => setFilterOpen(false)}
                  >
                    Готово
                  </button>
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}

      <CalendarSessionSheet
        open={Boolean(sheet)}
        day={sheet?.day}
        sessions={filtered}
        groups={groups}
        branches={branches}
        doctors={doctors}
        sessionId={sheet?.sessionId || null}
        onSelectSession={(id) =>
          setSheet((prev) => (prev ? { ...prev, sessionId: id } : null))
        }
        onClose={closeDaySheet}
        onUpdated={load}
      />

      <SessionDetailModal
        sessionId={detailId}
        open={Boolean(detailId)}
        onClose={() => {
          setDetailId(null);
          load();
        }}
        onUpdated={load}
        branches={branches}
        doctors={doctors}
      />

      {!isAdminApp ? (
      <Modal
        open={Boolean(createKind)}
        onClose={() => setCreateKind(null)}
        title={calendarEntryTitle(createKind)}
        footer={
          <Button type="submit" form="cal-create-form" disabled={createSaving}>
            {createSaving ? "…" : "Создать"}
          </Button>
        }
      >
        <form id="cal-create-form" onSubmit={submitCreate} className={s.calEditForm}>
          {createKind === "event" ? (
            <>
              <Field label="Название">
                <Input
                  value={createForm.title}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, title: e.target.value })
                  }
                  required
                />
              </Field>
              <Field label="Тип события">
                <Select
                  value={createForm.event_kind}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, event_kind: e.target.value })
                  }
                >
                  <option value="competition">Соревнование</option>
                  <option value="meeting">Собрание</option>
                  <option value="other">Другое</option>
                </Select>
              </Field>
            </>
          ) : null}
          {createKind === "individual" ? (
            <Field label="Клиент">
              <ClientSearchPicker
                value={createForm.patient_id}
                onChange={(patient_id) =>
                  setCreateForm({ ...createForm, patient_id })
                }
              />
            </Field>
          ) : null}
          {createKind === "training" ? (
            <Field label={L.group}>
              <Select
                value={createForm.specialization_id}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    specialization_id: e.target.value,
                  })
                }
                required
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <SessionDateTimeFields
            date={createForm.sessionDate}
            startTime={createForm.startTime}
            endTime={createForm.endTime}
            onChange={(patch) => setCreateForm({ ...createForm, ...patch })}
          />
          <Field label="Зал">
            <Select
              value={createForm.branch_id}
              onChange={(e) =>
                setCreateForm({ ...createForm, branch_id: e.target.value })
              }
            >
              <option value="">—</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
          {!staffMode ? (
            <Field label={L.trainer}>
              <Select
                value={createForm.doctor_id}
                onChange={(e) =>
                  setCreateForm({ ...createForm, doctor_id: e.target.value })
                }
                required
              >
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.user?.full_name || d.full_name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <Field label="Заметка">
            <Input
              value={createForm.notes}
              onChange={(e) =>
                setCreateForm({ ...createForm, notes: e.target.value })
              }
            />
          </Field>
        </form>
      </Modal>
      ) : null}
    </div>
  );
}
